import { z } from "zod";

/**
 * No-show risk scoring — a transparent, deterministic statistical model (NOT an
 * LLM). Predicting which booked appointments are likely to no-show lets the
 * clinic prioritise reminder calls / confirmations and consider light
 * overbooking. Kept explainable on purpose: the score is reproducible and every
 * point is attributable to a named factor the staff can see and trust.
 *
 * Signals (from the patient's own history + the booking):
 *  - historical no-show rate = no_shows / reached (completed + no_show)
 *  - repeat no-shows (a strong, well-evidenced predictor)
 *  - new patient with no history (first visits no-show more than established)
 *  - long lead time between booking and the appointment
 */

export const noShowTierSchema = z.enum(["low", "medium", "high"]);
export type NoShowTier = z.infer<typeof noShowTierSchema>;

export const noShowFactorSchema = z.object({
	code: z.enum([
		"repeat_no_shows",
		"high_history_rate",
		"new_patient",
		"long_lead_time",
	]),
	/** Optional numeric detail the UI can show (count, percent, days). */
	value: z.number().optional(),
});
export type NoShowFactor = z.infer<typeof noShowFactorSchema>;

export const noShowRiskResultSchema = z.object({
	tier: noShowTierSchema,
	/** 0–100, higher = more likely to no-show. */
	score: z.number().int().min(0).max(100),
	factors: z.array(noShowFactorSchema),
});
export type NoShowRiskResult = z.infer<typeof noShowRiskResultSchema>;

export const appointmentNoShowRiskSchema = z.object({
	appointment_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	tier: noShowTierSchema,
	score: z.number().int().min(0).max(100),
	factors: z.array(noShowFactorSchema),
});
export type AppointmentNoShowRisk = z.infer<typeof appointmentNoShowRiskSchema>;

export interface NoShowRiskFeatures {
	/** Past appointments that reached a conclusion: completed + no_show. */
	priorReached: number;
	/** How many of those were no-shows. */
	priorNoShows: number;
	/** Days between booking (created) and the scheduled time. */
	leadTimeDays: number;
}

const clamp = (n: number, lo: number, hi: number) =>
	Math.max(lo, Math.min(hi, n));

/**
 * Pure scoring. Deterministic — same features always yield the same result.
 * Thresholds are pinned by unit tests; tune there, not by feel.
 */
export function noShowRisk(f: NoShowRiskFeatures): NoShowRiskResult {
	const factors: NoShowFactor[] = [];
	let score = 0;

	if (f.priorReached <= 0) {
		// No history — first visits skew toward no-show.
		score += 25;
		factors.push({ code: "new_patient" });
	} else {
		const rate = f.priorNoShows / f.priorReached;
		score += Math.round(rate * 75);
		if (f.priorNoShows >= 2) {
			score += 20;
			factors.push({ code: "repeat_no_shows", value: f.priorNoShows });
		} else if (rate >= 0.3) {
			factors.push({
				code: "high_history_rate",
				value: Math.round(rate * 100),
			});
		}
	}

	const lead = Math.max(0, Math.round(f.leadTimeDays));
	if (lead >= 21) {
		score += 18;
		factors.push({ code: "long_lead_time", value: lead });
	} else if (lead >= 10) {
		score += 9;
	}

	score = clamp(Math.round(score), 0, 100);
	const tier: NoShowTier =
		score >= 50 ? "high" : score >= 25 ? "medium" : "low";
	return { tier, score, factors };
}
